import {
  paymentMethod as PaymentMethods,
  paymentMode as PaymentModes,
  paymentType as PaymentTypes
} from '@ourbranch/lookups';
import { appConfig } from 'brand-config';

/**
 * Direct-bill ('D') is a mutual-only AUTO payment method. Migrated mutual auto policies (CFM) come in
 * as Direct pay and need it; Branch/BIX retail auto must never offer it. The brand flag
 * `featureFlags.autoDirectBillPaymentMethod` is on for the mutual brands that write auto (CFM), off for
 * Branch (#35123). Every auto payment-method dropdown — offer settings, the policy Billing tab, its
 * renewal counterpart — must run its option list through `filterAutoDirectBillOptions` so Direct only
 * ever appears for the mutuals, and the backend re-checks the same rule at bind (pre-bind-validations).
 */
export const isAutoDirectBillAllowed = () => Boolean(appConfig?.featureFlags?.autoDirectBillPaymentMethod);

// Drop the direct-billed methods — Direct ('D') and Bill to other ('B', #36200) — from an AUTO
// payment-method option list unless the brand enables auto direct bill. Callers pass only auto lists
// here; farm lists both for every brand and is not routed through this.
const directBilledAutoMethods = [PaymentMethods.Direct, PaymentMethods.BillToOther];
export const filterAutoDirectBillOptions = (options = []) =>
  isAutoDirectBillAllowed() ? options : options.filter((option) => !directBilledAutoMethods.includes(option.id));

// The auto 2-payment plan ('S') mirrors the mutuals' GO pay plans (#36200); Branch auto stays Monthly /
// One Time. Every auto payment-frequency dropdown runs its option list through this.
export const filterAutoPaymentTypeOptions = (options = []) =>
  isAutoDirectBillAllowed() ? options : options.filter((option) => option.id !== PaymentTypes.HalfTerm);

// On the brands that direct-bill auto, the billing mode follows the method exactly as it does on the
// mutual farm lines (#36200): Direct and Bill to other are direct-billed, card/ACH are automated.
export const autoPaymentModeForMethod = (method) =>
  directBilledAutoMethods.includes(method) ? PaymentModes.Direct : PaymentModes.Automated;

// Frequency choices for a given auto payment method, mirroring farm: Direct is never Monthly and Bill to
// other is One Time only. Branch keeps its existing list (Monthly / One Time for every method).
export const getAutoPaymentTypeOptionsForMethod = ({ options = [], method }) => {
  const brandOptions = filterAutoPaymentTypeOptions(options);
  if (!isAutoDirectBillAllowed()) {
    return brandOptions;
  }
  if (method === PaymentMethods.BillToOther) {
    return brandOptions.filter((option) => option.id === PaymentTypes.OneTime);
  }
  if (method === PaymentMethods.Direct) {
    return brandOptions.filter((option) => option.id !== PaymentTypes.Monthly);
  }
  return brandOptions;
};

// Direct + Monthly isn't a plan the mutuals sell (GO has no Direct Bill monthly for new business). A
// migrated policy that somehow carries it must be moved to a supported plan, so the Billing tab flags it.
export const isUnofferedAutoPlan = ({ method, type }) =>
  isAutoDirectBillAllowed() && method === PaymentMethods.Direct && type === PaymentTypes.Monthly;
